import {useEffect, useRef, useLayoutEffect} from "react";
import * as echarts from "echarts";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {MomentUtils} from "core/utils/MomentUtils";
import useCustomTranslation from "core/customTranslation";
import translations from "./translations";

const COLORS = {
    yes: "#28a745",
    no: "#dc3545",
    none: "#c9ccd1",
};

const TRUCK_PATH =
    "M0 48C0 21.5 21.5 0 48 0H368c26.5 0 48 21.5 48 48V96h50.7c17 0 33.3 6.7 45.3 18.7L589.3 192c12 12 18.7 28.3 18.7 45.3V256v32 64c17.7 0 32 14.3 32 32s-14.3 32-32 32H576c0 53-43 96-96 96s-96-43-96-96H256c0 53-43 96-96 96s-96-43-96-96H48c-26.5 0-48-21.5-48-48V48zM416 256H544V237.3L466.7 160H416v96zM160 464a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm368-48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0z";

const FleetRouteGantt = ({
    route,
    session,
    executed = [],
    telemetry = [],
    cursorMs = null,
    playing = false,
    playbackRange = null,
    pathInfo = null,
    onPlayPause = null,
    onReset = null,
    ganttUpdaterRef = null,
    refreshing = false,
    onRefresh = null,
}) => {
    const chartContainer = useRef(null);
    const chartInstance = useRef(null);
    const lastTruckMsRef = useRef(null);
    const {t: _Tf} = useTranslation("forms");
    const {t: _CT} = useCustomTranslation(translations);
    const {momentTz} = MomentUtils();

    const toMs = (value) => {
        if (value == null) return null;
        return momentTz(value)?.valueOf() ?? null;
    };

    const gpsEndMs = telemetry?.[telemetry.length - 1]?.time ? telemetry[telemetry.length - 1].time * 1000 : null;

    const stops = [...executed]
        .filter((w) => w.arrived_at)
        .map((w) => ({...w, ms: toMs(w.arrived_at)}))
        .filter((w) => w.ms != null)
        .sort((a, b) => a.ms - b.ms);

    const routeEndMs =
        toMs(session?.returned_at) ||
        toMs(session?.completed_at) ||
        toMs(session?.closed_at) ||
        toMs(session?.started_at) ||
        gpsEndMs ||
        stops[stops.length - 1]?.ms;

    const stopsRow = _Tf("labels.stops");

    const categories = stops.length ? [stopsRow] : [];

    const canPlay = !!pathInfo && pathInfo.totalDist > 0;

    const stopMarkers = [];
    stops.forEach((s) => {
        const label = s.island_name || s.waypoint_name || `${_Tf("labels.waypoint")} ${s.order}`;
        const answer = s.answer;
        const color = answer === "no" ? COLORS.no : answer === "yes" ? COLORS.yes : COLORS.none;
        const reasonText = s.reason?.customText || s.reason?.note || s.reason?.reasonLabel || s.reason?.reason || null;
        const tip = [
            `<strong>${label}</strong>`,
            `${_Tf("labels.arrived")}: ${momentTz(s.arrived_at)?.format("DD/MM/YYYY HH:mm")}`,
            s.weight != null ? `${_Tf("labels.kilos")}: ${s.weight} kg` : null,
            reasonText ? `${_Tf("labels.reason")}: ${reasonText}` : null,
        ]
            .filter(Boolean)
            .join("<br/>");
        stopMarkers.push({
            value: [s.ms, stopsRow],
            itemStyle: {color},
            _tip: tip,
        });
    });

    const truckTimeMs = lastTruckMsRef.current != null ? lastTruckMsRef.current : cursorMs != null ? cursorMs : null;

    useLayoutEffect(() => {
        if (chartInstance.current) {
            setTimeout(() => {
                chartInstance.current?.resize();
            }, 100);
        }
    });

    useEffect(() => {
        // el contenedor solo existe con paradas: al refrescar puede aparecer o desaparecer
        if (!chartContainer.current) {
            if (chartInstance.current) {
                chartInstance.current.dispose();
                chartInstance.current = null;
            }
            return;
        }
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartContainer.current, null, {
                renderer: "canvas",
                useDirtyRect: false,
            });
        }

        const allTimes = [
            ...stops.map((s) => s.ms),
            routeEndMs,
            playbackRange?.start,
            playbackRange?.end,
            gpsEndMs,
        ].filter((t) => t != null);

        const minT = allTimes.length ? Math.min(...allTimes) : undefined;
        let maxT = allTimes.length ? Math.max(...allTimes) : undefined;
        if (minT != null && maxT === minT) {
            maxT = minT + 60000;
        }

        const option = {
            tooltip: {
                trigger: "item",
                appendToBody: true,
                confine: true,
                formatter: (params) => params.data?._tip || params.name || "",
            },
            grid: {left: "58px", right: "20px", bottom: "40px", top: "10px", containLabel: true},
            xAxis: {
                type: "time",
                min: minT,
                max: maxT,
                splitLine: {show: true, lineStyle: {color: "#d8d8d8", width: 1, type: "dashed"}},
                axisLabel: {formatter: "{HH}:{mm}", hideOverlap: true},
            },
            yAxis: {
                type: "category",
                data: categories,
                inverse: true,
                axisLabel: {width: 54, overflow: "truncate"},
            },
            dataZoom: [
                {type: "inside", start: 0, end: 100, zoomOnMouseWheel: true},
                {type: "slider", start: 0, end: 100, bottom: 10, height: 18, showDetail: false},
            ],
            series: [
                ...(stops.length
                    ? [
                          {
                              name: _Tf("labels.stops"),
                              type: "line",
                              symbol: "none",
                              lineStyle: {color: "#adb5bd", type: "dashed", width: 1},
                              tooltip: {show: false},
                              data: stops.map((s) => [s.ms, stopsRow]),
                          },
                          {
                              name: _Tf("labels.stops"),
                              type: "scatter",
                              symbol: "circle",
                              symbolSize: 12,
                              emphasis: {focus: "series", scale: 1.3},
                              data: stopMarkers,
                          },
                      ]
                    : []),
                ...(truckTimeMs != null && categories.length
                    ? [
                          {
                              id: "truck",
                              name: _Tf("labels.truck"),
                              type: "scatter",
                              symbol: `path://${TRUCK_PATH}`,
                              symbolSize: 20,
                              symbolOffset: [0, 0],
                              symbolRotate: 0,
                              itemStyle: {color: "#22c55e"},
                              z: 100,
                              markLine: {
                                  silent: true,
                                  symbol: ["none", "none"],
                                  lineStyle: {color: "#22c55e", type: "dashed", width: 1.5},
                                  label: {show: false},
                                  data: [{xAxis: truckTimeMs}],
                              },
                              data: [
                                  {
                                      value: [truckTimeMs, stopsRow],
                                      _tip: `<strong>${_Tf("labels.truck")}</strong><br/>${momentTz(new Date(truckTimeMs))?.format("DD/MM/YYYY HH:mm")}`,
                                  },
                              ],
                          },
                      ]
                    : []),
            ],
        };

        chartInstance.current.setOption(option, true);
    }, [stops, categories, stopMarkers, truckTimeMs, _Tf, routeEndMs, gpsEndMs, playbackRange]);

    useEffect(() => {
        if (!ganttUpdaterRef || !stopsRow) return;
        ganttUpdaterRef.current = (ms) => {
            const chart = chartInstance.current;
            if (!chart || !categories.length || ms == null) return;
            lastTruckMsRef.current = ms;
            chart.setOption(
                {
                    series: [
                        {
                            id: "truck",
                            type: "scatter",
                            data: [
                                {
                                    value: [ms, stopsRow],
                                    _tip: `<strong>${_Tf("labels.truck")}</strong><br/>${momentTz(new Date(ms))?.format("DD/MM/YYYY HH:mm")}`,
                                },
                            ],
                            markLine: {data: [{xAxis: ms}]},
                        },
                    ],
                },
                false
            );
        };
        return () => {
            if (ganttUpdaterRef) ganttUpdaterRef.current = null;
        };
    }, [ganttUpdaterRef, stopsRow, categories.length]);

    const header = (
        <div className="d-flex align-items-center mb-2">
            <div className="fw-semibold text-secondary flex-grow-1">{_Tf("labels.timeline")}</div>
            <div className="d-flex align-items-center gap-1">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    title={playing ? _Tf("labels.pause") : _Tf("labels.play")}
                    disabled={!canPlay}
                    onClick={() => onPlayPause && onPlayPause()}>
                    <FontAwesomeIcon icon={playing ? "pause" : "play"} />
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    title={_Tf("labels.reset")}
                    disabled={!canPlay}
                    onClick={() => onReset && onReset()}>
                    <FontAwesomeIcon icon="backward-step" />
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    title={_CT("refresh")}
                    disabled={refreshing}
                    onClick={() => onRefresh && onRefresh()}>
                    <FontAwesomeIcon icon="arrows-rotate" spin={refreshing} />
                </button>
            </div>
        </div>
    );

    if (categories.length === 0) {
        return (
            <div className="mt-3">
                {header}
                <div className="small text-muted">{_Tf("labels.no-events")}</div>
            </div>
        );
    }

    return (
        <div className="mt-3">
            {header}
            <div
                ref={chartContainer}
                style={{width: "100%", height: "160px", maxWidth: "100%", overflow: "hidden"}}
            />
        </div>
    );
};
export default FleetRouteGantt;